import { useMemo } from "react";
import { browserTimezone, COMMON_TIMEZONES, supportedTimezones, timezoneLabel } from "../lib/tz";

/**
 * Timezone picker for the Control Center. The app schedules and displays every
 * post time in the chosen zone; it defaults to the browser's own zone, so a user
 * who never touches this sees no change. Common zones are grouped first for quick
 * access, with the full IANA list below (native selects support type-to-find).
 */
export function TimezoneSetting({
  timezone,
  onSetTimezone,
}: {
  timezone: string;
  onSetTimezone: (tz: string) => void;
}) {
  const all = useMemo(() => supportedTimezones(), []);
  // Everything except the common set, so the two groups don't repeat entries.
  const rest = useMemo(() => {
    const common = new Set(COMMON_TIMEZONES);
    return all.filter((z) => !common.has(z));
  }, [all]);
  const browser = browserTimezone();
  const onBrowserZone = timezone === browser;
  // A stored zone the current runtime doesn't list (rare) still needs an option
  // so the select can show it as selected rather than silently falling back.
  const orphan = !all.includes(timezone) && !COMMON_TIMEZONES.includes(timezone);

  const label = (z: string) => z.replace(/_/g, " ");

  return (
    <div className="card">
      <h2>Timezone</h2>
      <p className="muted small">
        Times you schedule are entered and shown in this zone. It defaults to your browser's
        zone — <strong>{label(browser)}</strong> — so you only need to change it if you want to
        post on a different zone's clock.
      </p>
      <div className="tz-row">
        <select
          className="tz-select"
          value={timezone}
          aria-label="Timezone"
          onChange={(e) => onSetTimezone(e.target.value)}
        >
          {orphan && <option value={timezone}>{label(timezone)}</option>}
          <optgroup label="Common">
            {COMMON_TIMEZONES.map((z) => (
              <option key={z} value={z}>
                {label(z)}
              </option>
            ))}
          </optgroup>
          <optgroup label="All timezones">
            {rest.map((z) => (
              <option key={z} value={z}>
                {label(z)}
              </option>
            ))}
          </optgroup>
        </select>
        {!onBrowserZone && (
          <button
            type="button"
            className="btn ghost small"
            onClick={() => onSetTimezone(browser)}
            title={`Switch back to your browser's zone, ${label(browser)}`}
          >
            Use browser zone
          </button>
        )}
      </div>
      <p className="muted small tz-current">
        Currently <strong>{timezoneLabel(timezone)}</strong>. A post you schedule for, say, 6:00 PM
        will publish at 6:00 PM in this zone.
      </p>
    </div>
  );
}
